import React, { FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { UseAutoComplete } from '../../hooks/useAutoComplete';
import { SearchCategory } from '../../types/enums';
import { AutoComplete } from '../forms/Autocomplete';
import { NearMe } from '../forms/NearMe';
import { useHasMounted } from '../../hooks/useHasMounted';

interface LocationSearchProps {
  category: SearchCategory;
  defaultMultiValue?: string;
  noIcon?: boolean;
  placeholder?: string;
}
const StyledHeroSearchInput = styled.div`
  position: relative;
`;

const StyledNearMe = styled.div`
  ${({ theme: { space, layers } }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: ${space.md};
    z-index: ${layers.searchLocation};
    height: ${space.lg};

    button {
      height: ${space.lg};
      width: ${space.lg};
      display: grid;
      place-items: center;
    }
  `}
`;

export const LocationSearch: FC<LocationSearchProps> = ({
  category,
  defaultMultiValue,
  noIcon,
  placeholder,
  ...rest
}) => {
  const { register, control, setValue, resetField, watch } = useFormContext();
  const {
    autoCompleteSuggestions,
    handleAutoComplete,
    loading: autoCompleteLoading,
  } = UseAutoComplete({ category, type: 'BASIC' });

  const hasMounted = useHasMounted();

  useEffect(() => {
    register('lat', { valueAsNumber: true });
    register('long', { valueAsNumber: true });
    register('nearme');
  }, [register]);

  const multiSearch = watch('multiSearch');
  const lat = watch('lat');
  const long = watch('long');

  // split the lat and lnf out of the location
  const handleLocationChange = (location) => {
    if (location?.lat) {
      setValue('lat', parseFloat(location?.lat as string), { shouldDirty: true });
    }

    if (location?.long) {
      setValue('long', parseFloat(location?.long as string), { shouldDirty: true });
    }

    if (location?.lat && location?.long) {
      setValue('nearme', true, { shouldDirty: true });
    }
  };

  // if the user uses the multisearch we need to reset the location
  useEffect(() => {
    if (multiSearch) {
      resetField('lat');
      resetField('long');
      setValue('nearme', false, { shouldDirty: true });
    }
  }, [multiSearch, resetField, setValue]);

  return (
    <StyledHeroSearchInput {...rest}>
      <Controller
        name="multiSearch"
        control={control}
        render={({ field: { onChange } }) => (
          <AutoComplete
            defaultValue={defaultMultiValue}
            suggestions={autoCompleteSuggestions}
            handleAutoComplete={handleAutoComplete}
            handleSelectValue={(newValue) => onChange(newValue)}
            loading={autoCompleteLoading}
            placeholder={lat && long ? 'Current Location' : placeholder || 'Search by location'}
            noIcon={noIcon}
          />
        )}
      />
      {hasMounted && !multiSearch && (
        <StyledNearMe>
          <NearMe onChange={(newValue) => handleLocationChange(newValue)} checked={lat && long} />
        </StyledNearMe>
      )}
    </StyledHeroSearchInput>
  );
};
